import type { TranslationKey } from "@privacy/translations";
import type { ComponentProps, ComponentType, FC, ReactElement } from "react";
import { useMemo, useState } from "react";
import MuiAutocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import { useMutation } from "@tanstack/react-query";
import type { SxProps, Theme } from "@mui/material/styles";
import { Stack } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { useController, useFormContext } from "react-hook-form";
import {
  AUTOCOMPLETE_LOADING_OPTION,
  AutocompleteOption,
  createFilterAutocompleteOption,
  CreateNewAutocompleteOption,
  withCreateNewOption,
  type AutocompleteOptionWithMaybeIsNew,
} from "../../shared/Autocomplete";
import type { IconProps } from "../../shared/icons";
import type { Option } from "../types";
import type { InputProps } from "../DynamicInput";
import type { InlineInputState } from "../inlineForm/inputs/InlineInputState";
import { useInlineInputState } from "../inlineForm/inputs/InlineInputState";
import { DynamicShared } from "../../dynamicShared";
import { trackEvent } from "../../shared/Snowplow";
import { AutoCompleteTextField } from "./SingleAutocomplete";
import { isAnswerRequestEmpty } from "./isAnswerRequestEmpty";

type MultiAutocompleteProps = InputProps<string[]> & {
  options: Option[];
  isLoading?: boolean;
  renderIcon?: (props: IconProps, option?: Option) => ReactElement<IconProps> | undefined;
  /** Icon to render next to the option in list */
  renderIconOption?: (option: Option) => ReactElement<IconProps>;
  placeholderKey?: TranslationKey;
  inlineInputStateStyles?: Record<InlineInputState, SxProps<Theme>>;
  chipVariant: ComponentProps<typeof DynamicShared.Chip>["variant"];
  denseOptions?: boolean;
  onMouseOver?: () => void;
} & (
    | { noOptionsTextKey: TranslationKey }
    | { noOptionsTextKey?: never; CreateNewDialog: ComponentType<CreateNewDialogProps> }
  );

export type CreateNewDialogProps = { onClose: () => void; value: string; onCreated: (option: Option) => void };

const FALLBACK_VALUE: string[] = [];

export type CreateNewDialogState = { isOpen: true; value: string } | { isOpen: false };

export const MultiAutocomplete: FC<MultiAutocompleteProps> = props => {
  const {
    questionCard,
    isLoading,
    onSave,
    readonly,
    renderIcon,
    renderIconOption,
    inlineInputStateStyles,
    chipVariant,
    denseOptions,
    onMouseOver,
  } = props;
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const [createNewDialogState, setCreateNewDialogState] = useState<CreateNewDialogState>({ isOpen: false });

  const {
    field,
    fieldState: { isDirty },
  } = useController({ control, name: questionCard.id });

  const { inputState: inlineInputState, setIsEditing } = useInlineInputState(readonly);
  const {
    mutate: handleSave,
    isError,
    isPending: isSaving,
  } = useMutation({
    mutationFn: async () => isDirty && onSave(field.value).finally(() => setIsEditing(false)),
  });

  const value: string[] = field.value ?? FALLBACK_VALUE;

  const selectedOptions = useMemo(
    () =>
      value
        .map(val => props.options.find(option => option.value === val))
        .filter((option): option is AutocompleteOptionWithMaybeIsNew => Boolean(option)),
    [value, props.options]
  );

  const isEmpty = isAnswerRequestEmpty(selectedOptions.map(option => option.value));

  const options: AutocompleteOptionWithMaybeIsNew[] = isLoading ? [AUTOCOMPLETE_LOADING_OPTION] : props.options;

  const hasCreateNewDialog = "CreateNewDialog" in props;

  return (
    <Stack sx={{ width: "100%" }}>
      <MuiAutocomplete
        noOptionsText={"noOptionsTextKey" in props && props.noOptionsTextKey ? t(props.noOptionsTextKey) : undefined}
        clearIcon={null}
        disabled={readonly || isSaving}
        multiple
        openOnFocus
        onMouseOver={onMouseOver}
        id={questionCard.id}
        disableCloseOnSelect
        clearOnBlur
        filterOptions={(sourceOptions, params) => {
          const filtered = createFilterAutocompleteOption(sourceOptions, params);
          return hasCreateNewDialog ? withCreateNewOption(filtered, params.inputValue, t) : filtered;
        }}
        renderValue={(selected, getItemProps) => {
          return selected.map((option, index) => {
            const { onDelete, key, tabIndex } = getItemProps({ index });
            return (
              <DynamicShared.Chip
                key={key}
                renderIcon={props => renderIcon?.(props, option)}
                label={option.label}
                tabIndex={tabIndex}
                onDelete={onDelete}
                inputState={inlineInputState}
                variant={chipVariant}
              />
            );
          });
        }}
        onBlur={() => {
          setIsEditing(false);
          handleSave();
        }}
        onFocus={() => setIsEditing(true)}
        value={selectedOptions}
        onChange={(_, selectedOptions) => {
          const newOption = selectedOptions.find(option => option.isNew);
          if (newOption) {
            trackEvent.createNewOptionFromForm(questionCard.input.type);
            setCreateNewDialogState({ isOpen: true, value: newOption.value });
          } else {
            const selectedValues = selectedOptions.map(option => option.value);
            setValue(questionCard.id, selectedValues, { shouldDirty: true });
          }
        }}
        options={options}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return option.isNew ? (
            <CreateNewAutocompleteOption key={option.value} {...optionProps} option={option} />
          ) : (
            <AutocompleteOption
              key={option.value}
              {...optionProps}
              option={option}
              icon={renderIconOption?.(option)}
              dense={denseOptions}
            />
          );
        }}
        isOptionEqualToValue={(option, selected) =>
          option.value === AUTOCOMPLETE_LOADING_OPTION.value || option.value === selected.value
        }
        renderInput={params => (
          <AutoCompleteTextField
            params={params}
            isSaving={isSaving}
            inputRef={field.ref}
            onBlur={field.onBlur}
            isError={isError}
            isMandatory={questionCard.mandatory}
            inputState={inlineInputState}
            isEmpty={isEmpty}
            placeholderKey={"placeholderKey" in props ? props.placeholderKey : undefined}
            inlineInputStateStyles={inlineInputStateStyles}
            sx={{
              /** We do not want the input to take space when not editing, but it is needed to show empty placeholder as well*/ [`&.${autocompleteClasses.inputRoot}`]:
                { minHeight: "36px", gap: isEmpty ? 0 : 1 },
              [`& .${autocompleteClasses.input}`]: {
                height: inlineInputState === "editing" || isEmpty ? "auto" : 0,
                py: "0 !important",
              },
            }}
          />
        )}
      />
      {createNewDialogState.isOpen && hasCreateNewDialog && (
        <props.CreateNewDialog
          onClose={() => setCreateNewDialogState({ isOpen: false })}
          onCreated={option => setValue(questionCard.id, [...value, option.value], { shouldDirty: true })}
          value={createNewDialogState.value}
        />
      )}
    </Stack>
  );
};
